import React, { useState } from 'react';
import { Modal, Button, Row, Spinner, Col, Form } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { MatchAdmin } from '../../../models/interfaces/Match';

const INITIALFORMVALUES: { lightTeamScore: string; darkTeamScore: string } = {
    lightTeamScore: '',
    darkTeamScore: '',
};
const schema = yup.object({
    lightTeamScore: yup.string().required('Score Is Required').matches(/^\d+$/g, 'Invalid number'),
    darkTeamScore: yup.string().required('Score Is Required').matches(/^\d+$/g, 'Invalid number'),
});

interface Props {
    shouldShow: boolean;
    onHideModal: (scoreEntered: boolean) => void;
    matchAdmin: MatchAdmin;
}

const MatchResultModal = ({ shouldShow, onHideModal, matchAdmin }: Props) => {
    const [confirmFinalScore, setConfirmFinalScore] = useState<boolean>(false);
    const [submittingFinalScore, setSubmittingFinalScore] = useState<boolean>(false);
    const [submissionError, setSubmissionError] = useState<string | undefined>();

    const cancelConfirmFinalScore = () => {
        setConfirmFinalScore(false);
    };

    const handleSubmitScore = (
        lightTeamScore: number,
        darkTeamScore: number,
        resetForm: () => void
    ) => {
        setSubmittingFinalScore(true);

        matchAdmin
            .setMatchScore(lightTeamScore, darkTeamScore)
            .then(() => {
                resetForm();
                setConfirmFinalScore(false);
                onHideModal(true);
            })
            .catch((error) => {
                console.error(error);
                setSubmissionError('Score was not successfully saved. Please try again');
            })
            .finally(() => {
                setSubmittingFinalScore(false);
            });
    };

    return (
        <Modal
            show={shouldShow}
            onHide={() => {
                onHideModal(false);
            }}
        >
            <Modal.Header
                closeButton
                onHide={() => {
                    onHideModal(false);
                }}
            >
                <Modal.Title>{!confirmFinalScore ? 'Enter' : 'Confirm'} Final Score</Modal.Title>
            </Modal.Header>
            <Formik
                validationSchema={schema}
                initialValues={INITIALFORMVALUES}
                onSubmit={({ lightTeamScore, darkTeamScore }, actions) => {
                    let LTS = Number(lightTeamScore);
                    let DTS = Number(darkTeamScore);
                    if (Number.isNaN(LTS)) {
                        actions.setFieldError('lightTeamScore', 'Score is Not a Number');
                        actions.setSubmitting(false);
                        return;
                    } else if (Number.isNaN(DTS)) {
                        actions.setFieldError('darkTeamScore', 'Score is Not a Number');
                        actions.setSubmitting(false);
                        return;
                    }
                    if (!confirmFinalScore) {
                        setConfirmFinalScore(true);
                    } else {
                        handleSubmitScore(LTS, DTS, actions.resetForm);
                    }
                }}
            >
                {({ handleSubmit, handleChange, values, touched, errors }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Modal.Body>
                            {!confirmFinalScore ? (
                                <>
                                    <Form.Group as={Row} controlId="lightTeamScore">
                                        <Form.Label column xs={8} md={8}>
                                            {`${matchAdmin.match.lightCapTeam.name} Score:`}
                                        </Form.Label>
                                        <Col>
                                            <Form.Control
                                                type="number"
                                                name="lightTeamScore"
                                                value={values.lightTeamScore}
                                                onChange={handleChange}
                                                placeholder="Score"
                                                isValid={
                                                    touched.lightTeamScore && !errors.lightTeamScore
                                                }
                                                isInvalid={
                                                    touched.lightTeamScore &&
                                                    !!errors.lightTeamScore
                                                }
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.lightTeamScore}
                                            </Form.Control.Feedback>
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} controlId="lightTeamScore">
                                        <Form.Label column xs={8} md={8}>
                                            {`${matchAdmin.match.darkCapTeam.name} Score:`}
                                        </Form.Label>
                                        <Col>
                                            <Form.Control
                                                type="number"
                                                name="darkTeamScore"
                                                value={values.darkTeamScore}
                                                onChange={handleChange}
                                                placeholder="Score"
                                                isValid={
                                                    touched.darkTeamScore && !errors.darkTeamScore
                                                }
                                                isInvalid={
                                                    touched.darkTeamScore && !!errors.darkTeamScore
                                                }
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.darkTeamScore}
                                            </Form.Control.Feedback>
                                        </Col>
                                    </Form.Group>
                                </>
                            ) : (
                                <>
                                    <div
                                        style={{
                                            fontWeight:
                                                values.lightTeamScore > values.darkTeamScore
                                                    ? 'bold'
                                                    : 'normal',
                                        }}
                                    >
                                        {`${matchAdmin.match.lightCapTeam.name}: ${values.lightTeamScore}`}
                                    </div>
                                    <div
                                        style={{
                                            fontWeight:
                                                values.darkTeamScore > values.lightTeamScore
                                                    ? 'bold'
                                                    : 'normal',
                                        }}
                                    >
                                        {`${matchAdmin.match.darkCapTeam.name}: ${values.darkTeamScore}`}
                                    </div>
                                </>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            {/* mocked style similar to formik errors */}
                            {submissionError && (
                                <Col
                                    xs="12"
                                    className="text-right invalid-feedback"
                                    style={{ paddingRight: 0, display: 'block' }}
                                >
                                    {submissionError}
                                </Col>
                            )}
                            <Button
                                onClick={
                                    !confirmFinalScore
                                        ? () => {
                                              onHideModal(true);
                                          }
                                        : cancelConfirmFinalScore
                                }
                                variant="outline-primary"
                            >
                                {!confirmFinalScore ? 'Cancel' : 'Back'}
                            </Button>
                            <Button variant="success" type="submit">
                                {!confirmFinalScore ? (
                                    'Submit Final Score'
                                ) : submittingFinalScore ? (
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                ) : (
                                    'Confirm Score'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default MatchResultModal;
